/**
 * What a reference resolves to, once its state is taken into account.
 *
 * The three cases are genuinely different and the UI treats them differently:
 * a live row is just a name, a deleted one is history and says so, and an
 * orphan is a reference whose target the server could not find at all.
 */
export type RefDisplay = {
    /** The text to render. Never the empty string. */
    text: string;
    /** True when the row is gone — render muted. */
    isDeleted: boolean;
    /** True when the row is archived but live — render the chip. */
    isArchived: boolean;
    /**
     * True when the server resolved the id to nothing at all. The reference is
     * genuinely orphaned: hard-deleted out from under its referent, or written
     * by a bug. Worth a dev-time warning, because unlike a deleted row it is
     * not a state the product supports.
     */
    isOrphan: boolean;
};

/** How much of an unresolvable id to show. Enough to grep the database with. */
const ID_FRAGMENT = 8;

type RefLabelInput = {
    name: string | null | undefined;
    id?: string | null;
    deleted?: boolean;
    archived?: boolean;
    /** Localized "deleted". */
    deletedMarker: string;
    /** Localized "Unknown", used when nothing resolved. */
    unknownLabel: string;
    /**
     * What to render when the reference is legitimately absent rather than
     * unresolvable — "No branch" for a purchase that predates branch linking
     * (BOS-161). Supplying it is the caller saying "blank is a state this field
     * is allowed to be in", so the row is neither labelled Unknown nor warned
     * about in development.
     *
     * Absent means no name **and** no id. A blank name with an id is still an
     * orphan: something pointed somewhere and the server could not follow it.
     */
    absentLabel?: string;
};

/**
 * Resolves a reference to the text and the flags a label renders.
 *
 * <p>The one rule: **never render an empty string for a name.** A blank cell
 * reads as "this has no name", which is a different and much less useful claim
 * than "this used to be called Borjomi and has been deleted" — and it was what
 * the Sales page showed for months (bos-api BOS-122).</p>
 */
export const resolveRefDisplay = ({
    name,
    id,
    deleted = false,
    archived = false,
    deletedMarker,
    unknownLabel,
    absentLabel,
}: RefLabelInput): RefDisplay => {
    const trimmed = name?.trim() ?? '';

    // Nothing was referenced at all, and the caller says that is allowed. Not
    // an orphan: an orphan is a reference that failed to resolve, and there is
    // no reference here to fail.
    if (trimmed.length === 0 && !id && absentLabel) {
        return {
            text: absentLabel,
            isDeleted: false,
            isArchived: false,
            isOrphan: false,
        };
    }

    // No name at all: the server could not resolve the id. Show the fragment so
    // the row is traceable rather than merely blank.
    if (trimmed.length === 0) {
        const fragment = id ? id.slice(0, ID_FRAGMENT) : '';
        return {
            text: fragment ? `${unknownLabel} (${fragment}…)` : unknownLabel,
            isDeleted: true,
            isArchived: false,
            isOrphan: true,
        };
    }

    return {
        text: deleted ? `${trimmed} (${deletedMarker})` : trimmed,
        isDeleted: deleted,
        isArchived: archived && !deleted,
        isOrphan: false,
    };
};
